import './otp-input.scss';

import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

export interface IOtpInputProps {
  value: string;
  onChange: (value: string) => void;
  length?: number;
  error?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  label?: string;
}

const OtpInput = forwardRef<HTMLInputElement, IOtpInputProps>(
  (
    { value, onChange, length = 4, error, disabled, autoFocus, label },
    forwardedRef
  ) => {
    const ref = useRef<HTMLInputElement>(null);
    useImperativeHandle(forwardedRef, () => ref.current as HTMLInputElement);

    // autoFocus-атрибут после await (шаг OTP монтируется не в жесте тапа) на мобиле
    // клавиатуру не открывает. Повторный focus в следующем кадре это чинит.
    useEffect(() => {
      if (!autoFocus || disabled) return undefined;
      const id = window.setTimeout(() => ref.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }, [autoFocus, disabled]);

    const sanitize = (raw: string) => raw.replace(/\D/g, '').slice(0, length);

    const rootClass = ['otp-input', disabled ? 'otp-input--disabled' : '']
      .join(' ')
      .trim();

    return (
      <div className={rootClass} onClick={() => ref.current?.focus()}>
        <input
          ref={ref}
          className="otp-input__field"
          value={value}
          onChange={(e) => onChange(sanitize(e.target.value))}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="\d*"
          maxLength={length}
          disabled={disabled}
          autoFocus={autoFocus}
          aria-label={label}
          aria-invalid={error || undefined}
        />

        <div className="otp-input__cells" aria-hidden="true">
          {Array.from({ length }, (_, index) => {
            const char = value[index] ?? '';
            const isActive = index === value.length;
            const cellClass = [
              'otp-input__cell',
              char !== '' ? 'otp-input__cell--filled' : '',
              isActive ? 'otp-input__cell--active' : '',
              error ? 'otp-input__cell--error' : ''
            ]
              .join(' ')
              .trim();

            return (
              <div key={index} className={cellClass}>
                {char}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
);

OtpInput.displayName = 'OtpInput';

export default OtpInput;
